const MIRRORED_TOKEN_ASSET_PATH_RE =
  /^\/assets\/token-images\/[a-f0-9]{64}\.(png|jpe?g|webp|gif|svg)$/i
const URL_PARSE_BASE = 'https://wils.invalid'

export const TOKEN_IMAGE_LOAD_TIMEOUT_MS = 8_000
export const TOKEN_IMAGE_RETRY_DELAY_MS = 250
export const TOKEN_IMAGE_NEGATIVE_CACHE_TTL_MS = 30_000
const TOKEN_IMAGE_STATUS_CACHE_MAX = 512

export type TokenImageStatus = 'loaded' | 'failed'

interface TokenImageCacheEntry {
  status: TokenImageStatus
  expiresAt: number | null
}

export interface TokenImageStatusCache {
  get: (src: string, now?: number) => TokenImageStatus | undefined
  setLoaded: (src: string) => void
  setFailed: (src: string, now?: number) => void
  delete: (src: string) => void
}

function runtimeOrigin(): string | undefined {
  return typeof window === 'undefined' ? undefined : window.location.origin
}

function parsedOrigin(value?: string): string | undefined {
  if (!value) return undefined
  try {
    return new URL(value).origin
  } catch {
    return undefined
  }
}

function isLoopbackHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase()
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === '[::1]' ||
    normalized === '::1'
  )
}

function isUnsafeAssetSource(value: string): boolean {
  return (
    !value || value.includes('\\') || value.includes('%') || /(?:^|\/)\.{1,2}(?:\/|$)/.test(value)
  )
}

function parseAssetURL(value: string, currentOrigin?: string): URL | undefined {
  try {
    return new URL(value, parsedOrigin(currentOrigin) ?? URL_PARSE_BASE)
  } catch {
    return undefined
  }
}

function isAllowedAssetOrigin(url: URL, absolute: boolean, currentOrigin?: string): boolean {
  return !absolute || url.origin === parsedOrigin(currentOrigin) || isLoopbackHostname(url.hostname)
}

/**
 * Canonicalise one trusted mirrored token asset to the current app origin.
 *
 * Persisted development rows may still carry localhost:8081. We accept those
 * loopback origins, plus the app's own absolute origin, but always return only
 * the root-relative content-addressed path. Other origins never reach the DOM.
 */
export function canonicalizeMirroredTokenAssetURL(
  src?: string,
  currentOrigin: string | undefined = runtimeOrigin()
): string | undefined {
  const value = (src ?? '').trim()
  if (isUnsafeAssetSource(value)) return undefined

  const rootRelative = value.startsWith('/') && !value.startsWith('//')
  const absolute = /^https?:\/\//i.test(value) || value.startsWith('//')
  if (!rootRelative && !absolute) return undefined

  const url = parseAssetURL(value, currentOrigin)
  if (!url) return undefined
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined
  if (url.username || url.password || url.search || url.hash) return undefined
  if (!MIRRORED_TOKEN_ASSET_PATH_RE.test(url.pathname)) return undefined
  if (!isAllowedAssetOrigin(url, absolute, currentOrigin)) return undefined

  return url.pathname
}

export function isMirroredTokenAssetURL(src?: string): boolean {
  return canonicalizeMirroredTokenAssetURL(src) !== undefined
}

export function createTokenImageStatusCache(
  maxEntries = TOKEN_IMAGE_STATUS_CACHE_MAX,
  negativeTtlMs = TOKEN_IMAGE_NEGATIVE_CACHE_TTL_MS
): TokenImageStatusCache {
  const entries = new Map<string, TokenImageCacheEntry>()

  const write = (src: string, entry: TokenImageCacheEntry) => {
    entries.delete(src)
    entries.set(src, entry)
    while (entries.size > maxEntries) {
      const oldest = entries.keys().next().value
      if (oldest === undefined) break
      entries.delete(oldest)
    }
  }

  return {
    get(src, now = Date.now()) {
      const entry = entries.get(src)
      if (!entry) return undefined
      if (entry.expiresAt !== null && entry.expiresAt <= now) {
        entries.delete(src)
        return undefined
      }
      return entry.status
    },
    setLoaded(src) {
      write(src, { status: 'loaded', expiresAt: null })
    },
    setFailed(src, now = Date.now()) {
      write(src, { status: 'failed', expiresAt: now + negativeTtlMs })
    },
    delete(src) {
      entries.delete(src)
    },
  }
}

export const tokenImageStatusCache = createTokenImageStatusCache()
